import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import {
    BodyTextMedium,
    ComponentTextMedium,
    HeadingText50,
    HeadingText30,
    Modal,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { filterNullish } from '@modernhealth/filter-nullish';
import { useAnalytics } from '@modernhealth/web-analytics';
import { EventLexicon } from '@modernhealth/analytics-lexicon';

import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';
import {
    createModalPath,
    createModalRouteToolkit,
    useNavigateBackFromModalRoute,
} from 'src/shared/navigation/ModalRouteToolkit';
import { TopicGoal } from 'src/modules/pathways/components/PathwayGoals';
import { SessionNotFoundModal } from 'src/modules/pathways/components/SessionNotFoundModal';
import { PathwaySessionState } from 'src/generated/webapp_gql';
import { usePathwayQuery } from 'src/modules/pathways/documents/pathway.generated';
import { SessionCancellationPromptContent } from 'src/modules/videoSessions/components/SessionCancellationPromptContent';
import { createRenderSwitch } from 'src/shared/components/RenderSwitch';

const modalPath = createModalPath('session-detail');

enum ModalState {
    DETAIL,
    CANCEL,
}

const Switch = createRenderSwitch<ModalState>();

export const PathwaySessionDetailModalPage = () => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { closeModal } = useNavigateBackFromModalRoute(modalPath);
    const [modalState, setModalState] = useState(ModalState.DETAIL);
    const { uuid } = useParams<{ uuid: string }>();
    const { loading, data, refetch } = usePathwayQuery({
        variables: {
            uuid,
        },
    });

    if (loading) {
        return null;
    }

    if (!data?.pathway?.memberPathway) {
        return <SessionNotFoundModal isVisible onClose={closeModal} />;
    }

    const {
        pathway: { memberPathway },
    } = data;

    const topic = memberPathway.pathwayTopics[memberPathway.currentSessionIndex];

    if (
        !topic?.sessionDetail?.coachingVisit ||
        ![PathwaySessionState.Scheduled, PathwaySessionState.Joinable].includes(
            topic.sessionDetail.state,
        )
    ) {
        return <SessionNotFoundModal isVisible onClose={closeModal} />;
    }

    const {
        sessionDetail: { coachingVisit },
    } = topic;

    return (
        <Modal isVisible onCloseButtonClick={closeModal} maxWidth={558}>
            <Switch expression={modalState}>
                <Switch.Case expression={ModalState.CANCEL}>
                    <SessionCancellationPromptContent
                        session={coachingVisit}
                        onCancel={() => setModalState(ModalState.DETAIL)}
                        onSuccess={async () => {
                            await refetch();
                            closeModal();
                        }}
                    />
                </Switch.Case>
                <Switch.Default
                    render={() => {
                        const startTime = new Date(coachingVisit.scheduledTime);
                        const endTime = new Date(coachingVisit.scheduledTime);
                        endTime.setMinutes(
                            endTime.getMinutes() + (coachingVisit.scheduledDuration ?? 30),
                        );

                        return (
                            <>
                                <HeadingText50 marginBottom={6}>
                                    {t('pathways:pathway_detail.session.topic_title', {
                                        sessionNumber: memberPathway.currentSessionIndex + 1,
                                        topicTitle: topic.title,
                                    })}
                                </HeadingText50>
                                <BodyTextMedium marginBottom={16}>
                                    <FormattedSessionDateTimeRange
                                        start={startTime}
                                        end={endTime}
                                    />
                                </BodyTextMedium>
                                <LinkButton
                                    onClick={() => {
                                        analytics.trackEvent(
                                            EventLexicon.clickCancelSession({
                                                visitUuid: topic.sessionDetail?.coachingVisit?.uuid,
                                                providerUuid:
                                                    memberPathway.pathwayProvider.provider.uuid,
                                                creationSource: 'PATHWAYS',
                                                pathwayUuid: uuid,
                                                pathwayProgress: memberPathway.state,
                                            }),
                                        );

                                        setModalState(ModalState.CANCEL);
                                    }}
                                >
                                    <ComponentTextMedium>
                                        {t('pathways:action.cancel_session')}
                                    </ComponentTextMedium>
                                </LinkButton>
                                <BodyTextMedium marginBottom={28}>
                                    {topic.description}
                                </BodyTextMedium>
                                <HeadingText30>
                                    {t('pathways:components.session_modal.goals_title')}
                                </HeadingText30>
                                {filterNullish(topic.goals).map(goal => (
                                    <TopicGoal key={goal.title} goal={goal} />
                                ))}
                            </>
                        );
                    }}
                />
            </Switch>
        </Modal>
    );
};

const LinkButton = styled.button({
    appearance: 'none',
    border: 'none',
    background: 'none',
    boxShadow: 'none',
    padding: 0,
    color: colors.red55,
    marginBottom: 28,

    ':hover': {
        color: colors.red55,
    },
});

export const PathwaySessionDetailModalRouteToolkit = createModalRouteToolkit({
    modalPath,
    component: PathwaySessionDetailModalPage,
});
